<template>
  <b-container style="width:700px;">
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3 class="mb-0">Manage LTI 1.3 Credentials</h3>
        <b-button variant="success" size="sm" @click="showCreatePlatformModal = true">
          Create Platform
        </b-button>
      </div>

      <!-- Search Field -->
      <b-input-group class="mb-3">
        <b-input-group-prepend>
          <b-input-group-text>Search</b-input-group-text>
        </b-input-group-prepend>
        <b-form-input
          v-model="searchQuery"
          placeholder="Search by platform, school name, or client ID..."
          @keyup.enter="searchPlatforms"
          @input="handleSearchInput">
        </b-form-input>
        <b-input-group-append>
          <b-button variant="success" @click="searchPlatforms">Find
            <b-spinner v-if="isLoadingPlatforms" class="m-1" label="Spinning" small />
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <!-- Filter Options & Pagination Info -->
      <div class="mb-2 d-flex justify-content-between align-items-center">
        <div v-if="!isLoadingPlatforms && filteredPlatforms.length > 0" class="small text-muted">
          {{ paginationInfo }}
        </div>
        <b-form-checkbox
          v-model="showOnlyWithCourses"
          switch>
          <span class="small">Show only platforms with courses</span>
        </b-form-checkbox>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingPlatforms" class="text-center py-4">
        <b-spinner></b-spinner>
        <p class="mt-2">Loading platforms...</p>
      </div>

      <!-- Platform List -->
      <div v-else-if="platforms.length > 0">

        <div v-if="filteredPlatforms.length === 0 && searchQuery" class="text-center py-4 text-muted">
          <p>No results found matching "{{ searchQuery }}"</p>
        </div>
        <div v-for="platform in paginatedPlatforms" :key="platform.id" class="mb-3">
          <!-- Platform Header - Collapsible -->
          <div class="border rounded">
            <div
              class="p-3 d-flex justify-content-between align-items-center cursor-pointer"
              @click="togglePlatform(platform)"
              style="cursor: pointer; background-color: #f8f9fa;">
              <div>
                <strong>{{ getDomainFromIss(platform.iss) }}</strong>
              </div>
              <div>
                <span class="text-muted mr-2">{{ platform.credentials.length }} credential{{ platform.credentials.length !== 1 ? 's' : '' }} - {{ getTotalCoursesforPlatform(platform) }} course{{ getTotalCoursesforPlatform(platform) !== 1 ? 's' :'' }}</span>
                <b-icon :icon="platform.isExpanded ? 'chevron-up' : 'chevron-down'"></b-icon>
              </div>
            </div>

            <!-- Expanded Platform Details -->
            <b-collapse :visible="platform.isExpanded">
              <div class="p-3">
                <!-- Credentials -->
                <div>
                  <h6 class="mb-2"><strong>Credentials:</strong></h6>

                  <!-- No Credentials Message -->
                  <div v-if="!platform.credentials || platform.credentials.length === 0" class="text-info mb-3">
                    <b-icon icon="info-circle"></b-icon>
                    No credentials yet. Credentials will be auto-generated upon first LTI launch.
                  </div>

                  <!-- Credentials List -->
                  <div v-if="platform.credentials && platform.credentials.length > 0">
                  <div v-for="credential in platform.credentials" :key="credential.id" class="mb-2 border rounded">
                    <!-- Credential Header - Collapsible -->
                    <div
                      class="p-2 cursor-pointer d-flex justify-content-between align-items-center"
                      @click="toggleCredential(credential)"
                      style="cursor: pointer; background-color: #e9ecef;">
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{{ credential.school_name || credential.client_id }}</strong>
                          </div>
                          <div v-if="getCredentialDomains(credential).length > 0" class="text-muted small">
                            {{ getCredentialDomains(credential).join(', ') }}
                          </div>
                        </div>
                        <div class="small text-muted mt-1 d-flex justify-content-between">
                          <span>
                            <span v-if="credential.accepts_payment">Payment</span>
                            <span v-if="credential.accepts_payment && credential.accepts_access_code"> & </span>
                            <span v-if="credential.accepts_access_code">Access Code</span>
                          </span>
                          <span v-if="credential.deployments && credential.deployments.length > 0">
                            {{ getTotalCourses(credential) }} course{{ getTotalCourses(credential) !== 1 ? 's' : '' }} · {{ getTotalProcessedEnrollments(credential) }}/{{ getTotalEnrollments(credential) }} enrollments
                          </span>
                        </div>
                      </div>
                      <b-icon :icon="credential.isExpanded ? 'chevron-up' : 'chevron-down'" class="ml-2"></b-icon>
                    </div>

                    <!-- Expanded Credential Details -->
                    <b-collapse :visible="credential.isExpanded">
                      <div class="p-3 bg-light">
                        <ul class="pl-0 mb-2">
                          <li style="list-style-type:none">
                            Client ID: {{ credential.client_id }}
                          </li>
                          <li style="list-style-type:none; display: flex; align-items: center;" class="mt-2" @click.stop>
                            <span class="mr-2" style="white-space: nowrap;">School Name:</span>
                            <b-form-input
                              v-model="credential.school_name"
                              placeholder="Enter school name (optional)"
                              size="sm"
                              style="flex: 1;"
                              @input="markCredentialAsModified(credential)">
                            </b-form-input>
                          </li>
                          <li style="list-style-type:none" class="mt-2" v-if="getCredentialDomains(credential).length > 0">
                            Domain: {{ getCredentialDomains(credential).join(', ') }}
                            <b-icon
                              icon="info-circle"
                              class="ml-1 text-muted"
                              v-b-tooltip.hover
                              title="Domains extracted from course line_item_url">
                            </b-icon>
                          </li>
                          <li style="list-style-type:none; display: flex; align-items: center;" class="mt-2" @click.stop>
                            <span class="mr-2">Accepts Payment: </span>
                            <b-form-checkbox v-model="credential.accepts_payment" switch
                              @change="markCredentialAsModified(credential)">
                              {{ credential.accepts_payment ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                          </li>
                          <li style="list-style-type:none; display: flex; align-items: center;" class="mt-2" @click.stop>
                            <span class="mr-2">Accepts Access Code: </span>
                            <b-form-checkbox v-model="credential.accepts_access_code" switch
                              @change="markCredentialAsModified(credential)">
                              {{ credential.accepts_access_code ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                          </li>
                          <li style="list-style-type:none; display: flex; align-items: center;" class="mt-2" @click.stop>
                            <span class="mr-2">Shows Teacher Resources: </span>
                            <b-form-checkbox v-model="credential.shows_teacher_resources" switch
                              @change="markCredentialAsModified(credential)">
                              {{ credential.shows_teacher_resources ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                          </li>
                          <li style="list-style-type:none; display: flex; align-items: center;" class="mt-2" @click.stop>
                            <span class="mr-2">ASL Dictionary (WTS): </span>
                            <b-form-checkbox v-model="credential.allows_wts" switch
                              @change="markCredentialAsModified(credential)">
                              {{ credential.allows_wts ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                          </li>
                          <li style="list-style-type:none; margin-top: 10px;" @click.stop>
                            <b-input-group prepend="Auto-Activate Product" size="sm">
                              <b-form-select
                                v-model="credential.product_id"
                                :options="productOptions"
                                @change="markCredentialAsModified(credential)">
                              </b-form-select>
                            </b-input-group>
                            <small class="text-muted">Courses launched under this credential will be auto-activated with this product (applies to new and existing un-activated contexts)</small>
                          </li>
                          <li style="list-style-type:none; margin-top: 10px;" @click.stop>
                            <b-input-group prepend="Auto-Enroll Access Code" size="sm">
                              <b-form-input v-model.trim="credential.auto_enroll_coupon_code" type="text"
                                placeholder="e.g., SPRING25"
                                @input="markCredentialAsModified(credential)"></b-form-input>
                            </b-input-group>
                            <small v-if="credential.auto_enroll_coupon_code && credential.auto_enroll_product_code" class="text-muted d-block">
                              {{ credential.auto_enroll_product_code }} · {{ credential.auto_enroll_usage }} / {{ credential.auto_enroll_max_usage }}
                              {{ credential.auto_enroll_counts_unique_emails ? 'unique-email seats used' : 'seats used' }}
                            </small>
                            <small v-if="credential.auto_enroll_product_id && credential.product_id && credential.auto_enroll_product_id !== credential.product_id"
                                   class="text-warning d-block">
                              ⚠️ This code is for {{ credential.auto_enroll_product_code }}, but Auto-Activate Product differs.
                              Students won't be auto-enrolled until the products match.
                            </small>
                            <!-- TWA-275: both switches on for this credential. Deliberate for
                                 manually rostered premium KSS schools (TWA-283), so this names
                                 the consequence rather than flagging an error. -->
                            <!-- A plain div with v-if, not <b-alert>: b-alert kept a stale
                                 warning on screen when the condition went false (see Course.vue). -->
                            <div v-if="wtsAutoEnrollBoth(credential)" class="alert alert-warning mt-2 mb-1 py-2 px-2" role="alert">
                              <small>
                                <strong>ASL Dictionary is on and an auto-enroll code is set for this school.</strong>
                                Students reach the dictionary without signing up, so they will not be
                                enrolled, no seats will be claimed, and no grade is passed back — for every
                                course under this credential set to "Inherit from Credential", plus any
                                course with WTS enabled on itself.
                                Fine if this school is manually rostered; not fine if students are meant
                                to complete the Sign Up module.
                              </small>
                            </div>
                            <small class="text-muted d-block">
                              Binding here creates ONE seat pool shared by every matching course under this credential.
                              For a shared, district, or catch-all credential, bind at the COURSE level instead — Activate 1.3 Course — so each licensed course draws its own pool. A course-level code overrides this one.
                              A bound code switches to <b>auto-enroll mode</b>: students enroll with the code and are blocked at cap; payment/access-code settings are unused.
                            </small>
                          </li>
                        </ul>

                        <!-- Action Buttons -->
                        <div class="mt-3">
                          <div class="d-flex justify-content-between align-items-start">
                            <div>
                              <b-button
                                variant="success"
                                size="sm"
                                @click.stop="updateCredentialSettings(credential)"
                                :disabled="!canUpdateCredential(credential) || isUpdatingCredential">
                                <b-spinner v-if="isUpdatingCredential && currentUpdatingCredentialId === credential.id" small class="mr-1"></b-spinner>
                                Update Settings
                              </b-button>
                            </div>
                            <b-button
                              variant="secondary"
                              size="sm"
                              @click.stop="openCoursesModal(credential)"
                              v-if="credential.deployments && credential.deployments.length > 0">
                              View Courses ({{ getTotalCourses(credential) }})
                            </b-button>
                          </div>
                          <!-- TWA-944: refusals stay on screen (not a fading toast), full width under the buttons so they never wrap beside View Courses. -->
                          <div v-if="credential.saveRefusals && credential.saveRefusals.length" class="small text-danger mt-2">
                            <div v-for="r in credential.saveRefusals" :key="r.code">
                              <b-icon icon="exclamation-circle"></b-icon> {{ r.message }}
                            </div>
                            <b-form-checkbox v-if="credential.saveRefusals.some(r => r.code === 'current_courses_mismatch')"
                              v-model="credential.allowMismatchedCourses" class="mt-1 text-dark">
                              These courses use their own access codes (save anyway)
                            </b-form-checkbox>
                          </div>
                          <div v-if="!credential.accepts_payment && !credential.accepts_access_code" class="small text-danger mt-2">
                            <b-icon icon="exclamation-triangle"></b-icon>
                            At least one option must be enabled
                          </div>
                        </div>
                      </div>
                    </b-collapse>
                  </div>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div v-if="totalPages > 1" class="d-flex justify-content-center align-items-center mt-4">
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="currentPage === 1"
            @click="previousPage">
            <b-icon icon="chevron-left"></b-icon> Previous
          </b-button>

          <span class="mx-3 small">
            Page {{ currentPage }} of {{ totalPages }}
          </span>

          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="currentPage === totalPages"
            @click="nextPage">
            Next <b-icon icon="chevron-right"></b-icon>
          </b-button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-4 text-muted">
        <p v-if="hasSearched && searchQuery">No platforms found matching "{{ searchQuery }}"</p>
        <p v-else-if="hasSearched">No platforms found.</p>
        <p v-else>Click Find to load all platforms, or enter a search term.</p>
      </div>
    </b-card>

    <!-- Create Platform Modal -->
    <b-modal
      v-model="showCreatePlatformModal"
      title="Create New Platform"
      modal-class="create-platform-modal"
      @hidden="resetCreatePlatformForm">
      <b-form @submit.prevent="createPlatformAndCredential" class="mt-3">
        <b-form-group label-for="modal-domain">
          <template #label>
            Platform Issuer
            <b-icon
              icon="info-circle"
              class="ml-1 text-muted"
              v-b-tooltip.hover
              title="The LTI 1.3 issuer identifier. Enter the platform domain with or without https:// (e.g., canvas.university.edu or https://canvas.university.edu)">
            </b-icon>
          </template>
          <b-form-input
            id="modal-domain"
            v-model="form.domain"
            placeholder="learn.university.edu"
            required
            autocomplete="off">
          </b-form-input>
        </b-form-group>

        <b-form-group label-for="modal-platform-type">
          <template #label>
            Platform Type
            <b-icon
              icon="info-circle"
              class="ml-1 text-muted"
              v-b-tooltip.hover
              title="Select the LMS platform type. This determines the LTI endpoint URLs (authorization, JWKS, token grant). Canvas and D2L (Brightspace) use different endpoint structures.">
            </b-icon>
          </template>
          <b-form-radio v-model="form.d2l" :value="false">Canvas</b-form-radio>
          <b-form-radio v-model="form.d2l" :value="true">D2L (Brightspace)</b-form-radio>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showCreatePlatformModal = false">
          Cancel
        </b-button>
        <b-button
          variant="success"
          @click="createPlatformAndCredential"
          :disabled="!form.domain || isCreating">
          Create Platform
          <b-spinner v-if="isCreating" class="ml-1" small />
        </b-button>
      </template>
    </b-modal>

    <!-- Courses Modal -->
    <b-modal
      v-model="showCoursesModal"
      :title="selectedCredential ? (selectedCredential.school_name || selectedCredential.client_id) : ''"
      hide-footer
      body-class="courses-modal-body">
      <div v-if="selectedCredential">
        <!-- Course Search Field -->
        <b-form-input
          v-model="courseSearchQuery"
          placeholder="Search courses by title or context ID..."
          @input="filterCourses"
          class="mt-3 mb-3">
        </b-form-input>

        <!-- Course count / Results count -->
        <div v-if="courseSearchQuery && filteredCoursesCount > 0" class="small text-muted mb-2 d-flex justify-content-between">
          <span>Showing {{ filteredCoursesCount }} of {{ getTotalCourses(selectedCredential) }} courses</span>
          <span>{{ getTotalProcessedEnrollments(selectedCredential) }}/{{ getTotalEnrollments(selectedCredential) }} enrollments</span>
        </div>
        <div v-else-if="!courseSearchQuery" class="text-muted mb-2 d-flex justify-content-between">
          <span>{{ getTotalCourses(selectedCredential) }} course{{ getTotalCourses(selectedCredential) !== 1 ? 's' : '' }}</span>
          <span>{{ getTotalProcessedEnrollments(selectedCredential) }}/{{ getTotalEnrollments(selectedCredential) }} enrollments</span>
        </div>
        <div v-if="courseSearchQuery && filteredCoursesCount === 0" class="text-center text-muted py-3">
          No courses found matching "{{ courseSearchQuery }}"
        </div>

        <div v-if="selectedCredential.deployments && selectedCredential.deployments.length > 0">
          <div v-for="deployment in selectedCredential.deployments" :key="deployment.id" class="mb-3">
            <div v-for="context in deployment.contexts" :key="context.id" v-show="shouldShowCourse(context)" class="mb-2 p-2 border rounded">
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <div>{{ context.context_title || context.context_label }}</div>
                  <div class="small text-muted">Context ID: {{ context.context_id }}</div>
                </div>
                <div class="d-flex flex-column align-items-end text-right">
                  <div class="small mb-2">{{ context.processed_enrollments }}/{{ context.total_enrollments }} enrollments</div>
                  <b-button
                    v-if="context.token"
                    :to="`/activate_course/${context.token}`"
                    variant="success"
                    size="sm">
                    Activate
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted">
          No courses found for this credential.
        </div>
      </div>
    </b-modal>

  </b-container>
</template>

<script>
import {
  listPlatforms, createPlatform, updateCredential, getProducts
} from "../../utils/twa-api"

export default {
  data() {
    return {
      form: {
        domain: this.$route.params.domain || this.$route.query.domain || '',
        d2l: false
      },
      searchQuery: '',
      showOnlyWithCourses: true,
      courseSearchQuery: '',
      filteredCoursesCount: 0,
      currentPage: 1,
      itemsPerPage: 20,
      isCreating: false,
      isUpdatingCredential: false,
      currentUpdatingCredentialId: null,
      platforms: [],
      filteredPlatforms: [],
      isLoadingPlatforms: false,
      showCreatePlatformModal: false,
      showCoursesModal: false,
      selectedCredential: null,
      updateMessage: '',
      hasSearched: false,
      allProducts: []
    }
  },
  async mounted() {
    // Don't load platforms on mount - require search
    try {
      this.allProducts = await getProducts()
    } catch (e) {
      console.error('Failed to load products', e)
    }
    // Deep link (e.g. the Auto-Enroll (School) readout on Activate 1.3 Course):
    // ?search= prefills the box and runs the search so the school is on screen.
    if (this.$route.query.search) {
      this.searchQuery = this.$route.query.search
      await this.searchPlatforms()
    }
  },
  watch: {
    showOnlyWithCourses() {
      this.filterPlatforms()
    }
  },
  computed: {
    canCreatePlatform() {
      return !!this.form.domain
    },
    productOptions() {
      const options = [{ value: null, text: 'None (no auto-activate)' }]
      this.allProducts
        .filter(p => p.name.toLowerCase() !== 'zfree')
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach(p => options.push({ value: p.id, text: `${p.name} (${p.product_code})` }))
      return options
    },
    totalPages() {
      return Math.ceil(this.filteredPlatforms.length / this.itemsPerPage)
    },
    paginatedPlatforms() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredPlatforms.slice(start, end)
    },
    paginationInfo() {
      const start = (this.currentPage - 1) * this.itemsPerPage + 1
      const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredPlatforms.length)
      return `Showing ${start}-${end} of ${this.filteredPlatforms.length} platforms`
    }
  },
  methods: {
    // === Sanitization Utilities ===

    /**
     * Sanitize string input to prevent XSS attacks
     * Removes HTML tags, scripts, and dangerous characters
     */
    sanitizeString(str) {
      if (!str || typeof str !== 'string') return ''

      // Remove HTML tags
      let sanitized = str.replace(/<[^>]*>/g, '')

      // Remove script tags and event handlers
      sanitized = sanitized.replace(/javascript:/gi, '')
      sanitized = sanitized.replace(/on\w+\s*=/gi, '')

      // Encode special characters
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;'
      }
      sanitized = sanitized.replace(/[&<>"'/]/g, char => map[char])

      // Trim and limit length
      return sanitized.trim().slice(0, 255)
    },

    /**
     * Validate and sanitize client_id format
     * Most LMS use UUID or alphanumeric format
     */
    sanitizeClientId(clientId) {
      if (!clientId) return ''

      // Allow only alphanumeric, hyphens, and underscores
      const sanitized = clientId.replace(/[^a-zA-Z0-9-_]/g, '')

      return sanitized.trim().slice(0, 100)
    },

    /**
     * Sanitize domain/URL input
     */
    sanitizeDomain(domain) {
      if (!domain) return ''

      // Remove protocol if present
      let sanitized = domain.replace(/^https?:\/\//i, '')

      // Remove path, query, and fragment
      sanitized = sanitized.split('/')[0]
      sanitized = sanitized.split('?')[0]
      sanitized = sanitized.split('#')[0]

      // Allow only valid domain characters
      sanitized = sanitized.replace(/[^a-zA-Z0-9.-]/g, '')

      // Basic domain validation
      if (!/^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(sanitized)) {
        throw new Error('Invalid domain format')
      }

      return sanitized.trim().toLowerCase()
    },

    async searchPlatforms() {
      this.hasSearched = true
      // Sanitize search query before sending to API
      const sanitizedQuery = this.searchQuery ? this.sanitizeString(this.searchQuery) : null
      await this.loadPlatforms(sanitizedQuery)
    },

    handleSearchInput() {
      if (!this.searchQuery) {
        // Reset form state when search is cleared
        this.platforms = []
        this.filteredPlatforms = []
        this.hasSearched = false
      }
    },

    async loadPlatforms(query) {
      this.isLoadingPlatforms = true
      // Defensive: ensure products are loaded before the Auto-Activate Product
      // dropdown renders on each credential (mounted-time fetch may not have
      // resolved yet on a fast first click).
      if (this.allProducts.length === 0) {
        try { this.allProducts = await getProducts() } catch (e) { console.error('Failed to load products', e) }
      }
      try {
        const response = await listPlatforms(query)
        this.platforms = response.platforms || []
        // Initialize properties for each platform and credential
        this.platforms.forEach(platform => {
          this.$set(platform, 'isExpanded', false)
          this.$set(platform, 'showTechnicalDetails', false)
          if (platform.credentials) {
            platform.credentials.forEach(credential => {
              this.$set(credential, 'isExpanded', false)
              this.$set(credential, 'hasUnsavedChanges', false)
              // Store original values for comparison
              this.$set(credential, 'original_school_name', credential.school_name)
              this.$set(credential, 'original_accepts_payment', credential.accepts_payment)
              this.$set(credential, 'original_accepts_access_code', credential.accepts_access_code)
              this.$set(credential, 'original_shows_teacher_resources', credential.shows_teacher_resources)
              this.$set(credential, 'original_allows_wts', credential.allows_wts)
              this.$set(credential, 'original_product_id', credential.product_id || null)
              this.$set(credential, 'auto_enroll_coupon_code', credential.auto_enroll_coupon_code || '')
              this.$set(credential, 'original_auto_enroll_coupon_code', credential.auto_enroll_coupon_code || '')
            })
          }
        })
        // Apply filters (including showOnlyWithCourses default)
        this.filterPlatforms()
      } catch (error) {
        console.error('Error loading platforms:', error)
        this.platforms = []
        this.filteredPlatforms = []

        this.$bvToast.toast('Failed to load platforms: ' + (error.response?.data?.message || error.message), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }
      this.isLoadingPlatforms = false
    },

    filterPlatforms() {
      if (!this.searchQuery && !this.showOnlyWithCourses) {
        this.filteredPlatforms = this.platforms
      } else {
        // Sanitize search query to prevent any potential issues
        const query = this.sanitizeString(this.searchQuery).toLowerCase()

        this.filteredPlatforms = this.platforms.filter(platform => {
          // Filter by search query if present
          if (this.searchQuery) {
            const domain = this.getDomainFromIss(platform.iss).toLowerCase()

            // Search in platform domain
            if (domain.includes(query)) return true

            // Search in credentials (school name, client_id, domain hints, context_id)
            if (platform.credentials) {
              const matchesCredential = platform.credentials.some(credential => {
                // Search school name
                if (credential.school_name && credential.school_name.toLowerCase().includes(query)) {
                  return true
                }

                // Search client ID
                if (credential.client_id && credential.client_id.toLowerCase().includes(query)) {
                  return true
                }

                // Search domain hints
                const domains = this.getCredentialDomains(credential)
                if (domains.some(d => d.toLowerCase().includes(query))) {
                  return true
                }

                // Search context IDs (course IDs)
                if (credential.deployments) {
                  const matchesContext = credential.deployments.some(deployment => {
                    if (deployment.contexts) {
                      return deployment.contexts.some(context => {
                        const contextId = (context.context_id || '').toLowerCase()
                        return contextId.includes(query)
                      })
                    }
                    return false
                  })
                  if (matchesContext) return true
                }

                return false
              })

              if (matchesCredential) return true
            }

            return false
          }

          return true
        }).map(platform => {
          // Clone platform to avoid mutating original
          const filteredPlatform = Object.assign({}, platform)

          if (platform.credentials) {
            let credentials = platform.credentials

            // Filter by search query if present
            if (this.searchQuery) {
              const domain = this.getDomainFromIss(platform.iss).toLowerCase()
              const platformDomainMatches = domain.includes(query)

              // If platform domain matches, show all credentials
              // Otherwise, filter to only show matching credentials
              if (!platformDomainMatches) {
                credentials = credentials.filter(credential => {
                  // Check school name
                  if (credential.school_name && credential.school_name.toLowerCase().includes(query)) {
                    return true
                  }

                  // Check client ID
                  if (credential.client_id && credential.client_id.toLowerCase().includes(query)) {
                    return true
                  }

                  // Check domain hints
                  const domains = this.getCredentialDomains(credential)
                  if (domains.some(d => d.toLowerCase().includes(query))) {
                    return true
                  }

                  // Check context IDs (course IDs)
                  if (credential.deployments) {
                    const matchesContext = credential.deployments.some(deployment => {
                      if (deployment.contexts) {
                        return deployment.contexts.some(context => {
                          const contextId = (context.context_id || '').toLowerCase()
                          return contextId.includes(query)
                        })
                      }
                      return false
                    })
                    if (matchesContext) return true
                  }

                  return false
                })
              }
            }

            // Filter by courses if enabled
            if (this.showOnlyWithCourses) {
              credentials = credentials.filter(credential => {
                // Keep only credentials that HAVE courses (contexts)
                const hasDeployments = credential.deployments && credential.deployments.length > 0
                const hasCourses = hasDeployments && credential.deployments.some(d =>
                  d.contexts && d.contexts.length > 0
                )
                return hasCourses
              })
            }

            filteredPlatform.credentials = credentials
          } else {
            filteredPlatform.credentials = platform.credentials
          }

          return filteredPlatform
        }).filter(filteredPlatform => {
          // Keep platforms with no credentials (new platforms waiting for first launch)
          const originalPlatform = this.platforms.find(p => p.id === filteredPlatform.id)
          const originalHadNoCredentials = !originalPlatform.credentials || originalPlatform.credentials.length === 0
          if (originalHadNoCredentials) return true

          // For platforms that originally had credentials, only keep if some remain after filtering
          return filteredPlatform.credentials && filteredPlatform.credentials.length > 0
        })
      }
      this.currentPage = 1 // Reset to page 1 when filtering
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    togglePlatform(platform) {
      this.$set(platform, 'isExpanded', !platform.isExpanded)
    },

    toggleCredential(credential) {
      this.$set(credential, 'isExpanded', !credential.isExpanded)
    },

    togglePlatformTechnicalDetails(platform) {
      this.$set(platform, 'showTechnicalDetails', !platform.showTechnicalDetails)
    },

    getTotalCoursesforPlatform(platform) {
      let total = 0
      if (platform.credentials) {
        platform.credentials.forEach(credential => {
          if (credential.deployments) {
            credential.deployments.forEach(deployment => {
              if (deployment.contexts) {
                total += deployment.contexts.length
              }
            })
          }
        })
      }
      return total
    },

    getTotalCourses(credential) {
      if (!credential.deployments) return 0
      let total = 0
      credential.deployments.forEach(deployment => {
        if (deployment.contexts) {
          total += deployment.contexts.length
        }
      })
      return total
    },

    getTotalEnrollments(credential) {
      if (!credential.deployments) return 0
      let total = 0
      credential.deployments.forEach(deployment => {
        if (deployment.contexts) {
          deployment.contexts.forEach(context => {
            total += context.total_enrollments || 0
          })
        }
      })
      return total
    },

    getTotalProcessedEnrollments(credential) {
      if (!credential.deployments) return 0
      let total = 0
      credential.deployments.forEach(deployment => {
        if (deployment.contexts) {
          deployment.contexts.forEach(context => {
            total += context.processed_enrollments || 0
          })
        }
      })
      return total
    },

    getCredentialDomains(credential) {
      if (!credential.deployments) return []

      const domainsSet = new Set()
      credential.deployments.forEach(deployment => {
        if (deployment.contexts) {
          deployment.contexts.forEach(context => {
            if (context.domains && context.domains.length > 0) {
              context.domains.forEach(domain => domainsSet.add(domain))
            }
          })
        }
      })

      return Array.from(domainsSet).sort()
    },

    openCoursesModal(credential) {
      this.selectedCredential = credential
      this.courseSearchQuery = ''
      this.filteredCoursesCount = this.getTotalCourses(credential)
      this.showCoursesModal = true
    },

    shouldShowCourse(context) {
      if (!this.courseSearchQuery) {
        return true
      }
      const query = this.courseSearchQuery.toLowerCase()
      const title = (context.context_title || context.context_label || '').toLowerCase()
      const contextId = (context.context_id || '').toLowerCase()

      // Also search in domains
      let domainMatch = false
      if (context.domains && context.domains.length > 0) {
        domainMatch = context.domains.some(domain =>
          domain.toLowerCase().includes(query)
        )
      }

      return title.includes(query) || contextId.includes(query) || domainMatch
    },

    filterCourses() {
      if (!this.selectedCredential || !this.selectedCredential.deployments) {
        this.filteredCoursesCount = 0
        return
      }

      let count = 0
      this.selectedCredential.deployments.forEach(deployment => {
        if (deployment.contexts) {
          deployment.contexts.forEach(context => {
            if (this.shouldShowCourse(context)) {
              count++
            }
          })
        }
      })
      this.filteredCoursesCount = count
    },

    clearCourseSearch() {
      this.courseSearchQuery = ''
      if (this.selectedCredential) {
        this.filteredCoursesCount = this.getTotalCourses(this.selectedCredential)
      }
    },

    async createPlatformAndCredential() {
      this.isCreating = true
      this.updateMessage = ''

      if (!this.form.domain) {
        this.isCreating = false
        return
      }

      try {
        // Sanitize domain input
        const sanitizedDomain = this.sanitizeDomain(this.form.domain)

        // Create platform without credential
        const response = await createPlatform(
          sanitizedDomain,
          this.form.d2l
        )
        this.updateMessage = response.message

        // Close modal on success
        this.showCreatePlatformModal = false

        // Show success message
        this.$bvToast.toast('Platform created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })

        // Search for the newly created platform
        this.searchQuery = sanitizedDomain
        await this.loadPlatforms(sanitizedDomain)
      } catch (error) {
        console.error('Error creating platform:', error)
        this.updateMessage = 'Error creating platform: ' + (error.response?.data?.message || error.message)

        this.$bvToast.toast(this.updateMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }

      this.isCreating = false
    },

    // TWA-275: both switches on for this credential. A method rather than a computed
    // because this screen renders a list of credentials. Reads the live (edited)
    // values, so the note appears as the second switch is flipped, not after saving.
    //
    // Gated on a change from the saved row (hand-off point 2): an existing both-set
    // credential is usually deliberate after the premium-KSS sweep (TWA-283), so
    // expanding it shows no badge.
    wtsAutoEnrollBoth(credential) {
      const code = (credential.auto_enroll_coupon_code || '').trim()
      if (!credential.allows_wts || !code) return false

      return credential.allows_wts !== credential.original_allows_wts ||
        code !== (credential.original_auto_enroll_coupon_code || '')
    },
    markCredentialAsModified(credential) {
      // Check if values have changed from original
      const hasChanges =
        credential.school_name !== credential.original_school_name ||
        credential.accepts_payment !== credential.original_accepts_payment ||
        credential.accepts_access_code !== credential.original_accepts_access_code ||
        credential.shows_teacher_resources !== credential.original_shows_teacher_resources ||
        credential.allows_wts !== credential.original_allows_wts ||
        credential.product_id !== credential.original_product_id ||
        credential.auto_enroll_coupon_code !== credential.original_auto_enroll_coupon_code

      this.$set(credential, 'hasUnsavedChanges', hasChanges)
      // TWA-944: a confirmation answers one refusal; any edit retires both.
      this.$set(credential, 'saveRefusals', [])
      this.$set(credential, 'allowMismatchedCourses', false)
    },

    canUpdateCredential(credential) {
      // Check if at least one option is enabled
      const hasAtLeastOne = credential.accepts_payment || credential.accepts_access_code

      // Check if values have changed from original
      const hasChanges =
        credential.school_name !== credential.original_school_name ||
        credential.accepts_payment !== credential.original_accepts_payment ||
        credential.accepts_access_code !== credential.original_accepts_access_code ||
        credential.shows_teacher_resources !== credential.original_shows_teacher_resources ||
        credential.allows_wts !== credential.original_allows_wts ||
        credential.product_id !== credential.original_product_id ||
        credential.auto_enroll_coupon_code !== credential.original_auto_enroll_coupon_code

      return hasAtLeastOne && hasChanges
    },

    async updateCredentialSettings(credential) {
      this.isUpdatingCredential = true
      this.currentUpdatingCredentialId = credential.id

      try {
        // Sanitize inputs before sending
        const sanitizedSchoolName = this.sanitizeString(credential.school_name)

        const result = await updateCredential(
          credential.id,
          sanitizedSchoolName,
          credential.accepts_payment,
          credential.accepts_access_code,
          credential.product_id,
          credential.shows_teacher_resources,
          credential.auto_enroll_coupon_code,
          credential.allows_wts,
          credential.allowMismatchedCourses
        )
        this.$set(credential, 'saveRefusals', [])
        this.$set(credential, 'allowMismatchedCourses', false)

        // Refresh auto-enroll fields from the server so the indicator/warning
        // reflect fresh state right after saving (rather than stale data until
        // a page reload). Keys are absent from the response when no code is
        // bound (e.g. after clearing), which correctly collapses the indicator.
        const updated = result && result.credential
        if (updated) {
          this.$set(credential, 'auto_enroll_coupon_code', updated.auto_enroll_coupon_code || '')
          this.$set(credential, 'auto_enroll_product_code', updated.auto_enroll_product_code)
          this.$set(credential, 'auto_enroll_product_id', updated.auto_enroll_product_id)
          this.$set(credential, 'auto_enroll_usage', updated.auto_enroll_usage)
          this.$set(credential, 'auto_enroll_max_usage', updated.auto_enroll_max_usage)
          this.$set(credential, 'auto_enroll_counts_unique_emails', updated.auto_enroll_counts_unique_emails)
        }

        // Update local state with sanitized value
        credential.school_name = sanitizedSchoolName

        // Update original values
        this.$set(credential, 'original_school_name', sanitizedSchoolName)
        this.$set(credential, 'original_accepts_payment', credential.accepts_payment)
        this.$set(credential, 'original_accepts_access_code', credential.accepts_access_code)
        this.$set(credential, 'original_shows_teacher_resources', credential.shows_teacher_resources)
        this.$set(credential, 'original_allows_wts', credential.allows_wts)
        this.$set(credential, 'original_product_id', credential.product_id || null)
        this.$set(credential, 'original_auto_enroll_coupon_code', credential.auto_enroll_coupon_code)
        this.$set(credential, 'hasUnsavedChanges', false)

        this.$bvToast.toast('Settings updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        })
      } catch (error) {
        console.error('Error updating settings:', error)

        // TWA-944: a refusal keeps the edits on screen with its reasons, so the admin can fix or confirm and save again.
        const refusals = error.response?.data?.refusals
        if (refusals && refusals.length) {
          this.$set(credential, 'saveRefusals', refusals)
          if (!('allowMismatchedCourses' in credential)) this.$set(credential, 'allowMismatchedCourses', false)
          this.isUpdatingCredential = false
          this.currentUpdatingCredentialId = null
          return
        }

        // Revert changes
        credential.school_name = credential.original_school_name
        credential.accepts_payment = credential.original_accepts_payment
        credential.accepts_access_code = credential.original_accepts_access_code
        credential.shows_teacher_resources = credential.original_shows_teacher_resources
        credential.allows_wts = credential.original_allows_wts
        credential.product_id = credential.original_product_id
        credential.auto_enroll_coupon_code = credential.original_auto_enroll_coupon_code
        this.$set(credential, 'hasUnsavedChanges', false)
        this.$set(credential, 'saveRefusals', [])
        this.$set(credential, 'allowMismatchedCourses', false)

        this.$bvToast.toast('Error updating settings: ' + (error.response?.data?.message || error.message), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        })
      }

      this.isUpdatingCredential = false
      this.currentUpdatingCredentialId = null
    },

    getDomainFromIss(iss) {
      return iss.replace('https://', '').replace('http://', '')
    },
    resetCreatePlatformForm() {
      this.form.domain = ''
      this.form.d2l = false
      this.updateMessage = ''
    }
  }
}
</script>

<style>
.courses-modal-body {
  max-height: 60vh;
  overflow-y: auto;
}

/* Increase tooltip width for platform endpoints */
.tooltip-inner {
  max-width: 400px !important;
}
</style>

<style scoped>
.border {
  border: 1px solid #dee2e6 !important;
}
.bg-light {
  background-color: #f8f9fa !important;
}
.small {
  font-size: 0.875rem;
}
.strikethrough {
  text-decoration: line-through;
}
</style>